import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import jsPDF from "jspdf";
import QRCode from "qrcode";

import shortLogo from "assets/images/logos/ShortLogo.png";
import { api, get, post } from "lib/api";
import { closeCompletedBatch, closeWorkOrderClientSide, fetchBuildabilitySummary } from "services/workOrders";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosFilterBar, IosPage, iosColors } from "mobile/components/IosParity";

const normalizeEntity = (value) => {
  if (!value) return null;
  if (value?.attributes) return { id: value.id, documentId: value.documentId || value.id, ...value.attributes };
  return { ...value, documentId: value.documentId || value.id };
};

const normalizeRelationList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(normalizeEntity).filter(Boolean);
  if (Array.isArray(value?.data)) return value.data.map(normalizeEntity).filter(Boolean);
  if (value?.data && typeof value.data === "object") return [normalizeEntity(value.data)].filter(Boolean);
  return [];
};

const entityId = (value) => value?.documentId || null;

const isCompletedUnitProgress = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  return ["completed", "complete", "passed", "pass", "done", "sold"].includes(normalized);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const getErrorMessage = (err, fallback) => {
  const raw =
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;
  if (/insufficient core inventory/i.test(String(raw))) {
    return `Not enough core inventory to generate tags. ${raw}`;
  }
  return String(raw || fallback);
};

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const successActionSx = {
  bgcolor: iosColors.green,
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#0D6149", boxShadow: "none" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&.Mui-disabled": { bgcolor: "rgba(20,122,92,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&.Mui-disabled": { borderColor: "rgba(181,71,8,0.15)", color: "rgba(15,15,18,0.36)" },
};

const buildTrackingUrlsForIdentifier = (identifier) => {
  const value = String(identifier || "").trim();
  if (!value) return { registrationUrl: "" };
  const encoded = encodeURIComponent(value);
  let base = "";
  const apiBase = String(api?.defaults?.baseURL || "").trim();
  if (apiBase.startsWith("http://") || apiBase.startsWith("https://")) base = apiBase;
  if (!base && typeof window !== "undefined") base = window.location.origin;
  if (!base) base = "https://vbspecialties.com";
  return { registrationUrl: `${base}/register/valve-body/${encoded}?qr=1` };
};

const packagingCopy = (language = "en") => {
  const isSpanish = language === "es";
  return {
    stopTitle: isSpanish ? "ALTO" : "STOP",
    stopInstruction: isSpanish ? "Escanee este QR primero para registrar la garantía" : "Scan this QR first to register for warranty",
    stopWarning: isSpanish ? "La garantía requiere registro completo." : "Warranty requires completed registration.",
    tagLabel: isSpanish ? "Etiqueta" : "Tag",
    partNumberLabel: isSpanish ? "Número de Parte" : "Part Number",
    workOrderLabel: isSpanish ? "Orden de Trabajo" : "Work Order",
    qcReportTitle: isSpanish ? "REPORTE DE CONTROL DE CALIDAD" : "QC REPORT",
    requiredTestsTitle: isSpanish ? "Pruebas Requeridas" : "Required Tests",
    qualityChecksTitle: isSpanish ? "Inspecciones de Calidad" : "Quality Checks",
    additionalInfoTitle: isSpanish ? "Información Adicional" : "Additional Information",
    latestStageLabel: isSpanish ? "Última Etapa" : "Latest Stage",
    completedLabel: isSpanish ? "Completado" : "Completed",
    noRequiredTests: isSpanish ? "No hay pruebas requeridas." : "No required tests on build sheet.",
    noQualityChecks: isSpanish ? "No hay inspecciones registradas." : "No quality checks on build sheet.",
    qrUnavailable: isSpanish ? "QR no disponible" : "QR unavailable",
    scanInstallSell: isSpanish ? "Escanee para registro de instalación o venta" : "Scan for install/sell registration flow",
    bendLineLabel: isSpanish ? "LÍNEA DE DOBLEZ" : "BEND LINE",
    trackingUnavailable: isSpanish ? "URL de rastreo no disponible" : "Tracking URL unavailable",
  };
};

export default function WorkOrderBuildMobile() {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logoDataUrlRef = useRef("");
  const [activeTab, setActiveTab] = useState("build");
  const [busyKey, setBusyKey] = useState("");
  const [batchQtyByLine, setBatchQtyByLine] = useState({});
  const [languagePrompt, setLanguagePrompt] = useState({ open: false, kind: "", row: null });
  const fromTab = searchParams.get("from") === "completed" ? "completed" : "active";

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-work-order-build", documentId],
    queryFn: () =>
      get(`/api/work-orders/${documentId}`, {
        populate: {
          items: {
            populate: {
              inventoryItem: true,
              buildSheet: {
                populate: {
                  requiredTests: { populate: { testEntries: true } },
                  qualityChecks: true,
                  additionalInformation: { populate: { documents: true } },
                },
              },
            },
          },
          units: {
            populate: {
              workOrderItem: {
                populate: {
                  inventoryItem: true,
                  buildSheet: {
                    populate: {
                      requiredTests: { populate: { testEntries: true } },
                      qualityChecks: true,
                      additionalInformation: { populate: { documents: true } },
                    },
                  },
                },
              },
              finishedGood: true,
            },
          },
        },
      }),
  });

  const workOrder = data?.data ? normalizeEntity(data.data) : null;
  const items = useMemo(() => normalizeRelationList(workOrder?.items), [workOrder]);
  const woRef = workOrder?.documentId || documentId;
  const statusIsClosed = ["complete", "completed", "shipped"].includes(String(workOrder?.status || "").toLowerCase());

  const { data: buildability } = useQuery({
    queryKey: ["m-work-order-buildability", woRef],
    queryFn: () => fetchBuildabilitySummary(woRef),
    enabled: !!woRef,
    staleTime: 1000,
  });

  const buildabilityByLine = useMemo(() => {
    const map = new Map();
    (buildability?.lines || []).forEach((line) => {
      if (line.workOrderItemDocumentId) map.set(line.workOrderItemDocumentId, line);
    });
    return map;
  }, [buildability]);

  const { data: fallbackUnitsRaw } = useQuery({
    queryKey: ["m-work-order-valve-body-units", woRef],
    queryFn: async () => {
      const numeric = Number(woRef);
      const orFilters = [];
      if (Number.isFinite(numeric)) orFilters.push({ workOrder: { id: { $eq: numeric } } });
      if (woRef) orFilters.push({ workOrder: { documentId: { $eq: String(woRef) } } });
      if (!orFilters.length) return { data: [] };
      return get("/api/valve-bodies", {
        publicationState: "preview",
        filters: { $or: orFilters },
        sort: "createdAt:asc",
        pagination: { pageSize: 1000 },
        populate: {
          workOrderItem: {
            populate: {
              inventoryItem: true,
              buildSheet: {
                populate: {
                  requiredTests: { populate: { testEntries: true } },
                  qualityChecks: true,
                  additionalInformation: { populate: { documents: true } },
                },
              },
            },
          },
          finishedGood: true,
        },
      });
    },
    enabled: !!woRef,
    staleTime: 1000,
  });

  const units = useMemo(() => {
    const byKey = new Map();
    const addUnit = (raw) => {
      const unit = normalizeEntity(raw);
      if (!unit) return;
      const key = String(unit.documentId || unit.id || unit.serialNumber || unit.optionalTag || "");
      if (!key || byKey.has(key)) return;
      byKey.set(key, unit);
    };
    normalizeRelationList(workOrder?.units).forEach(addUnit);
    normalizeRelationList(fallbackUnitsRaw?.data).forEach(addUnit);
    return Array.from(byKey.values());
  }, [workOrder?.units, fallbackUnitsRaw]);

  const unitIds = useMemo(() => units.map((unit) => String(entityId(unit))).filter(Boolean), [units]);

  const { data: inventoryOrdersRaw } = useQuery({
    queryKey: ["m-inventory-orders-for-wo", woRef],
    queryFn: () => {
      const numeric = Number(woRef);
      const orFilters = [];
      if (Number.isFinite(numeric)) orFilters.push({ workOrder: { id: { $eq: numeric } } });
      orFilters.push({ workOrder: { documentId: { $eq: String(woRef) } } });
      return get("/api/inventory-orders", {
        filters: { $or: orFilters },
        sort: "createdAt:desc",
        pagination: { pageSize: 20 },
        populate: { workOrder: true },
      });
    },
    enabled: !!woRef,
    staleTime: 5000,
  });

  const { data: stageRecordsData } = useQuery({
    queryKey: ["m-work-order-stage-records", id, unitIds.join(",")],
    queryFn: async () => {
      const numericIds = unitIds.map((uid) => Number(uid)).filter((n) => Number.isFinite(n));
      const docIds = unitIds.filter((uid) => !Number.isFinite(Number(uid)));
      const orFilters = [];
      if (numericIds.length > 0) orFilters.push({ unit: { id: { $in: numericIds } } });
      if (docIds.length > 0) orFilters.push({ unit: { documentId: { $in: docIds } } });
      if (!orFilters.length) return { data: [] };
      return get("/api/stage-records", {
        publicationState: "preview",
        populate: {
          unit: true,
          stage: true,
          testResults: true,
          qualityCheckValues: { populate: { images: true } },
          identifierValues: { populate: { images: true } },
        },
        sort: "createdAt:desc",
        filters: { $or: orFilters },
        pagination: { pageSize: 1000 },
      });
    },
    enabled: unitIds.length > 0,
    staleTime: 0,
  });

  const { data: warrantyClaimsData } = useQuery({
    queryKey: ["m-work-order-warranty-claims", id, unitIds.join(",")],
    queryFn: async () => {
      const numericIds = unitIds.map((uid) => Number(uid)).filter((n) => Number.isFinite(n));
      const docIds = unitIds.filter((uid) => !Number.isFinite(Number(uid)));
      const orFilters = [];
      if (numericIds.length > 0) orFilters.push({ unit: { id: { $in: numericIds } } });
      if (docIds.length > 0) orFilters.push({ unit: { documentId: { $in: docIds } } });
      if (!orFilters.length) return { data: [] };
      return get("/api/warranty-claims", {
        populate: { unit: true },
        sort: "createdAt:desc",
        filters: { $or: orFilters },
        pagination: { pageSize: 1000 },
      });
    },
    enabled: unitIds.length > 0,
    staleTime: 5000,
  });

  const inventoryOrders = useMemo(() => normalizeRelationList(inventoryOrdersRaw?.data), [inventoryOrdersRaw]);

  const latestStageByUnit = useMemo(() => {
    const map = new Map();
    const validUnitIds = new Set(unitIds);
    normalizeRelationList(stageRecordsData?.data).forEach((record) => {
      const unit = normalizeEntity(record?.unit?.data || record?.unit);
      const key = [String(unit?.documentId || ""), String(unit?.id || "")].find((candidate) => validUnitIds.has(candidate));
      if (!key) return;
      const previous = map.get(key);
      const currentDate = new Date(record?.createdAt || 0).getTime();
      const previousDate = new Date(previous?.createdAt || 0).getTime();
      if (!previous || currentDate >= previousDate) map.set(key, record);
    });
    return map;
  }, [stageRecordsData, unitIds]);

  const latestWarrantyByUnit = useMemo(() => {
    const map = new Map();
    const validUnitIds = new Set(unitIds);
    normalizeRelationList(warrantyClaimsData?.data).forEach((claim) => {
      const unit = normalizeEntity(claim?.unit?.data || claim?.unit);
      const key = [String(unit?.documentId || ""), String(unit?.id || "")].find((candidate) => validUnitIds.has(candidate));
      if (!key) return;
      const previous = map.get(key);
      const currentDate = new Date(claim?.createdAt || 0).getTime();
      const previousDate = new Date(previous?.createdAt || 0).getTime();
      if (!previous || currentDate >= previousDate) map.set(key, claim);
    });
    return map;
  }, [warrantyClaimsData, unitIds]);

  const isCompletedStatus = (status) => ["passed", "pass", "complete", "completed", "done"].includes(String(status || "").toLowerCase());

  const allUnitsCompleted = useMemo(() => {
    if (!units.length) return false;
    return units.every((unit) => {
      const stage =
        latestStageByUnit.get(String(unit.documentId || "")) ||
        latestStageByUnit.get(String(unit.id || ""));
      return isCompletedStatus(stage?.status) || isCompletedUnitProgress(unit?.lifecycleSummary?.progressStatus || unit?.progressStatus);
    });
  }, [latestStageByUnit, units]);

  const getUnitLineId = (unit) => {
    const line = normalizeEntity(unit?.workOrderItem?.data || unit?.workOrderItem);
    return entityId(line);
  };

  const getLineExisting = (line) => {
    const lineId = entityId(line);
    return units.filter((unit) => String(getUnitLineId(unit)) === String(lineId)).length;
  };

  const totalRemaining = items.reduce((sum, line) => Math.max(0, Number(line.quantity || 0) - getLineExisting(line)) + sum, 0);
  const totalCompleted = useMemo(() => units.filter((unit) => isCompletedUnitProgress(unit?.lifecycleSummary?.progressStatus || unit?.progressStatus)).length, [units]);
  const totalPlanned = items.reduce((sum, line) => sum + (Number(line.quantity || 0) || 0), 0);
  const canCloseCompletedBatch = !statusIsClosed && totalCompleted > 0 && totalCompleted < totalPlanned;

  const resolveUnitBuildSheet = (unit) => {
    const line = normalizeEntity(unit?.workOrderItem?.data || unit?.workOrderItem);
    const lineBuildSheet = normalizeEntity(line?.buildSheet?.data || line?.buildSheet);
    if (lineBuildSheet) return lineBuildSheet;
    const fallbackLine = items.find((item) => String(entityId(item)) === String(entityId(line)));
    return normalizeEntity(fallbackLine?.buildSheet?.data || fallbackLine?.buildSheet);
  };

  const getUnitPartNumber = (unit) => {
    const finishedGood = normalizeEntity(unit?.finishedGood?.data || unit?.finishedGood);
    if (finishedGood?.sku) return String(finishedGood.sku);
    const line = normalizeEntity(unit?.workOrderItem?.data || unit?.workOrderItem);
    const inventoryItem = normalizeEntity(line?.inventoryItem?.data || line?.inventoryItem);
    return inventoryItem?.sku ? String(inventoryItem.sku) : "";
  };

  const packagingRows = useMemo(
    () =>
      units.map((unit) => {
        const buildSheet = resolveUnitBuildSheet(unit);
        const latestStage =
          latestStageByUnit.get(String(unit.documentId || "")) ||
          latestStageByUnit.get(String(unit.id || ""));
        const latestWarranty =
          latestWarrantyByUnit.get(String(unit.documentId || "")) ||
          latestWarrantyByUnit.get(String(unit.id || ""));
        const progressStatus = unit?.lifecycleSummary?.progressStatus || unit?.progressStatus || "";
        const displayTag = String(unit.optionalTag || unit.serialNumber || unit.documentId || unit.id || "").trim();
        const completed = isCompletedStatus(latestStage?.status) || isCompletedUnitProgress(progressStatus);
        const activeWarranty = latestWarranty && !["closed", "denied"].includes(String(latestWarranty?.status || "").toLowerCase());
        const needs = [];
        if (!displayTag) needs.push("Tag required");
        if (!completed) needs.push("Complete tag before packaging");
        if (!buildSheet) needs.push("Build sheet missing");
        if (activeWarranty) needs.push(`Open RMA: ${latestWarranty?.status || "active"}`);
        const canPrint = !!displayTag && completed;
        return {
          key: String(entityId(unit)),
          unit,
          displayTag,
          partNumber: getUnitPartNumber(unit),
          buildSheet,
          requiredTests: normalizeRelationList(buildSheet?.requiredTests),
          qualityChecks: normalizeRelationList(buildSheet?.qualityChecks),
          additionalInformation: normalizeRelationList(buildSheet?.additionalInformation),
          latestStage: normalizeEntity(latestStage),
          latestWarranty: normalizeEntity(latestWarranty),
          progressStatus,
          completed,
          activeWarranty,
          needs,
          canPrint,
          ready: canPrint,
        };
      }),
    [latestStageByUnit, latestWarrantyByUnit, units, items]
  );
  const packageReadyCount = packagingRows.filter((row) => row.ready).length;

  const ensureQrDataUrl = async (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    try {
      return await QRCode.toDataURL(raw, {
        margin: 1,
        width: 768,
        errorCorrectionLevel: "M",
        color: { dark: "#000000", light: "#FFFFFF" },
      });
    } catch (err) {
      console.error("Failed to generate QR image", err);
      return null;
    }
  };

  const ensureLogoDataUrl = async () => {
    if (logoDataUrlRef.current) return logoDataUrlRef.current;
    try {
      const response = await fetch(shortLogo);
      if (!response.ok) return null;
      const blob = await response.blob();
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Failed to load logo"));
        reader.readAsDataURL(blob);
      });
      logoDataUrlRef.current = dataUrl;
      return dataUrl;
    } catch (err) {
      console.error("Failed to load label logo", err);
      return null;
    }
  };

  const downloadPackagingPacket = async (row, language = "en") => {
    const tag = String(
      row?.displayTag || row?.unit?.optionalTag || row?.unit?.serialNumber || row?.unit?.documentId || row?.unit?.id || ""
    ).trim();
    if (!tag) {
      alert("Packaging packet requires a tag.");
      return;
    }
    if (!row?.completed) {
      alert("Complete this tag before printing packaging documents.");
      return;
    }
    const copy = packagingCopy(language);
    setBusyKey(`${row.key}:packet`);
    try {
      const tracking = buildTrackingUrlsForIdentifier(tag);
      const qr = await ensureQrDataUrl(tracking.registrationUrl);
      const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
      const left = 0.55;
      const right = 7.95;
      let y = 0.7;

      pdf.setFillColor(245, 245, 245);
      pdf.rect(0, 0, 8.5, 11, "F");
      pdf.setTextColor(176, 0, 32);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(72);
      pdf.text(copy.stopTitle, 4.25, 2.1, { align: "center" });
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(18);
      pdf.text(copy.stopInstruction, 4.25, 2.85, { align: "center" });
      pdf.setFontSize(12);
      pdf.text(copy.stopWarning, 4.25, 3.25, { align: "center" });
      pdf.text(`${copy.tagLabel}: ${tag}`, 4.25, 3.65, { align: "center" });
      if (qr) pdf.addImage(qr, "PNG", 2.55, 4.05, 3.4, 3.4);
      pdf.setFontSize(9);
      pdf.text(copy.scanInstallSell, 4.25, 8.25, { align: "center" });

      pdf.addPage("letter", "portrait");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text(copy.qcReportTitle, right, y, { align: "right" });
      y += 0.35;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      [
        `${copy.tagLabel}: ${tag}`,
        `${copy.partNumberLabel}: ${row.partNumber || "-"}`,
        `${copy.workOrderLabel}: ${workOrder?.workOrderNumber || "-"}`,
        `${copy.latestStageLabel}: ${row.latestStage?.stage?.name || row.latestStage?.status || "-"}`,
        `${copy.completedLabel}: ${formatDateTime(row.latestStage?.completedAt || row.latestStage?.updatedAt)}`,
      ].forEach((line) => {
        pdf.text(line, left, y);
        y += 0.2;
      });

      y += 0.15;
      pdf.setFont("helvetica", "bold");
      pdf.text(copy.requiredTestsTitle, left, y);
      y += 0.18;
      pdf.setFont("helvetica", "normal");
      const stageResults = normalizeRelationList(row.latestStage?.testResults);
      const resultMap = new Map(stageResults.map((result) => [String(result?.testType || "").toLowerCase(), String(result?.result || "")]));
      if (!row.requiredTests.length) {
        pdf.text(copy.noRequiredTests, left, y);
        y += 0.18;
      } else {
        row.requiredTests.forEach((test, idx) => {
          if (y > 10.1) {
            pdf.addPage("letter", "portrait");
            y = 0.7;
          }
          const name = String(test?.testName || `Test ${idx + 1}`);
          const recorded = resultMap.get(name.toLowerCase()) || "recorded";
          pdf.text(`- ${name}: ${recorded}`, left, y);
          y += 0.18;
        });
      }

      y += 0.2;
      pdf.setFont("helvetica", "bold");
      pdf.text(copy.qualityChecksTitle, left, y);
      y += 0.18;
      pdf.setFont("helvetica", "normal");
      if (!row.qualityChecks.length) {
        pdf.text(copy.noQualityChecks, left, y);
        y += 0.18;
      } else {
        row.qualityChecks.forEach((check, idx) => {
          if (y > 10.1) {
            pdf.addPage("letter", "portrait");
            y = 0.7;
          }
          const name = String(check?.name || `Quality Check ${idx + 1}`);
          const wrapped = pdf.splitTextToSize(`- ${name}`, right - left);
          wrapped.forEach((line) => {
            pdf.text(line, left, y);
            y += 0.16;
          });
        });
      }

      if (row.additionalInformation.length) {
        if (y > 9.6) {
          pdf.addPage("letter", "portrait");
          y = 0.7;
        }
        y += 0.22;
        pdf.setFont("helvetica", "bold");
        pdf.text(copy.additionalInfoTitle, left, y);
        y += 0.18;
        pdf.setFont("helvetica", "normal");
        row.additionalInformation.forEach((entry, idx) => {
          if (y > 10.1) {
            pdf.addPage("letter", "portrait");
            y = 0.7;
          }
          const docs = normalizeRelationList(entry?.documents);
          const label = `${entry?.name || `Additional Information ${idx + 1}`} (${docs.length} document${docs.length === 1 ? "" : "s"})`;
          const wrapped = pdf.splitTextToSize(`- ${label}`, right - left);
          wrapped.forEach((line) => {
            pdf.text(line, left, y);
            y += 0.16;
          });
        });
      }

      pdf.save(`${tag}-packaging-packet.pdf`);
    } catch (err) {
      console.error("Failed to build mobile packaging packet", err);
      alert("Failed to generate packaging packet PDF.");
    } finally {
      setBusyKey("");
    }
  };

  const downloadLabel4x6 = async (row, language = "en") => {
    const tag = String(
      row?.displayTag || row?.unit?.optionalTag || row?.unit?.serialNumber || row?.unit?.documentId || row?.unit?.id || ""
    ).trim();
    if (!tag) {
      alert("Label generation requires a tag.");
      return;
    }
    if (!row?.completed) {
      alert("Complete this tag before printing labels.");
      return;
    }
    const copy = packagingCopy(language);
    setBusyKey(`${row.key}:label`);
    try {
      const tracking = buildTrackingUrlsForIdentifier(tag);
      const qr = await ensureQrDataUrl(tracking.registrationUrl);
      const logo = await ensureLogoDataUrl();
      const pdf = new jsPDF({ unit: "in", format: [4, 6], orientation: "portrait" });
      pdf.setLineWidth(0.02);
      pdf.rect(0.08, 0.08, 3.84, 5.84);
      if (logo) {
        const imageProps = pdf.getImageProperties(logo);
        const scale = Math.min(1.8 / Math.max(1, imageProps.width), 0.75 / Math.max(1, imageProps.height));
        pdf.addImage(logo, "PNG", 0.25, 0.3, imageProps.width * scale, imageProps.height * scale);
      } else {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(34);
        pdf.text("VBS", 0.3, 0.85);
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(23);
      let y = 1.7;
      pdf.splitTextToSize(row.partNumber || "PART-N/A", 3.35).forEach((line) => {
        pdf.text(line, 2, y, { align: "center" });
        y += 0.3;
      });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(`${copy.tagLabel}: ${tag}`, 2, y + 0.08, { align: "center" });
      pdf.setDrawColor(120, 120, 120);
      pdf.setLineWidth(0.01);
      pdf.line(0.2, 2.22, 3.8, 2.22);
      pdf.setFontSize(7.5);
      pdf.text(copy.bendLineLabel, 3.78, 2.17, { align: "right" });
      if (qr) pdf.addImage(qr, "PNG", 1.05, 2.65, 1.9, 1.9);
      else pdf.text(copy.qrUnavailable, 1.45, 3.55);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("TAG TRACKING QR", 2, 4.82, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.text(copy.scanInstallSell, 2, 5.0, { align: "center" });
      pdf.splitTextToSize(tracking.registrationUrl || copy.trackingUnavailable, 3.4).slice(0, 3).forEach((line, idx) => {
        pdf.text(line, 2, 5.18 + idx * 0.12, { align: "center" });
      });
      pdf.save(`${tag}-4x6-label.pdf`);
    } catch (err) {
      console.error("Failed to build mobile label", err);
      alert("Failed to generate 4x6 label PDF.");
    } finally {
      setBusyKey("");
    }
  };

  const generateMutation = useMutation({
    mutationFn: ({ lineId, quantity, overrideBuildabilityWarning }) =>
      post(`/api/work-order-items/${lineId}/generate-tags`, { quantity, overrideBuildabilityWarning }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order-build", documentId] });
      qc.invalidateQueries({ queryKey: ["m-pipeline-work-orders"] });
      qc.invalidateQueries({ queryKey: ["m-work-order-buildability", woRef] });
    },
    onError: async (err, variables) => {
      const status = Number(err?.response?.status || 0);
      const warningCode = err?.response?.data?.warningCode;
      if (status === 409 && warningCode === "buildability_required_material_shortage" && !variables?.overrideBuildabilityWarning) {
        const ok = window.confirm("Required build-sheet materials are short for this batch. Generate these etched tags anyway?");
        if (ok) {
          generateMutation.mutate({ ...variables, overrideBuildabilityWarning: true });
          return;
        }
      }
      alert(getErrorMessage(err, "Failed to generate tags."));
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeWorkOrderClientSide(workOrder?.documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order-build", documentId] });
      qc.invalidateQueries({ queryKey: ["m-pipeline-work-orders"] });
      if (workOrder?.documentId) navigate(`/m/work-orders/${workOrder.documentId}`);
    },
    onError: (err) => alert(`Cannot close work order. ${getErrorMessage(err, "Please address the incomplete items first.")}`),
  });

  const closeBatchMutation = useMutation({
    mutationFn: () => closeCompletedBatch(workOrder?.documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order-build", documentId] });
      qc.invalidateQueries({ queryKey: ["m-pipeline-work-orders"] });
      qc.invalidateQueries({ queryKey: ["m-work-order-buildability", woRef] });
      if (workOrder?.documentId) navigate(`/m/work-orders/${workOrder.documentId}`);
    },
    onError: (err) => alert(`Cannot close completed batch. ${getErrorMessage(err, "Please address the incomplete items first.")}`),
  });

  const handleLanguageChoice = (language) => {
    const { kind, row } = languagePrompt;
    setLanguagePrompt({ open: false, kind: "", row: null });
    if (!row) return;
    if (!row.completed || !row.canPrint) {
      alert("Complete this tag before printing packaging documents.");
      return;
    }
    if (kind === "label") downloadLabel4x6(row, language);
    else downloadPackagingPacket(row, language);
  };

  const goBack = () => navigate(`/m/pipeline${fromTab === "completed" ? "?tab=completed" : ""}`);

  if (error) {
    return (
      <IosPage>
        <MobileTopBar title="Build / Packaging" subtitle="Pipeline" onBack={goBack} />
        <Typography color="error" sx={{ mt: 2 }}>
          Failed to load work order.
        </Typography>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title="Build / Packaging"
        subtitle={workOrder?.workOrderNumber ? `WO ${workOrder.workOrderNumber}` : "Pipeline work order"}
        onBack={goBack}
      />

      {isLoading ? <LinearProgress sx={{ my: 1 }} /> : null}

      <IosFilterBar
        label="Workflow"
        value={activeTab}
        options={[
          { key: "build", label: "Build", icon: "qr_code_2", accent: "orange" },
          { key: "packaging", label: `Packaging ${packageReadyCount}/${packagingRows.length}`, icon: "inventory_2", accent: "green" },
        ]}
        onChange={setActiveTab}
      />

      <Stack spacing={0.8} sx={{ pb: 11 }}>
        <IosCard>
          <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between" spacing={0.75} alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1.15 }}>
                  {workOrder?.workOrderNumber || "Work Order"}
                </Typography>
                <Typography sx={{ color: iosColors.muted, fontSize: 12.5, fontWeight: 700 }}>
                  {workOrder?.customerRef || "No customer reference"}
                </Typography>
              </Box>
              <Chip size="small" variant="outlined" label={workOrder?.status || "active"} sx={{ fontWeight: 900 }} />
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${items.length} line${items.length === 1 ? "" : "s"}`} />
              <Chip size="small" label={`${units.length} tag${units.length === 1 ? "" : "s"}`} />
              <Chip size="small" label={`${totalRemaining} remaining`} color={totalRemaining ? "warning" : "success"} />
              {buildability ? (
                <Chip
                  size="small"
                  label={`Buildable ${buildability.totals?.buildableUnits ?? 0}/${buildability.totals?.plannedQty ?? 0}`}
                  color={buildability.totals?.hasShortage || buildability.totals?.hasMissingBuildSheet ? "warning" : "success"}
                />
              ) : null}
            </Stack>
          </Stack>
        </IosCard>

        {activeTab === "build" ? (
          <>
            <IosCard>
              <Stack spacing={0.7}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 900 }}>Inventory Orders</Typography>
                <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>
                  Component orders stay separate from Parts Bank orders and are created from valve body detail.
                </Typography>
                {inventoryOrders.length ? (
                  inventoryOrders.map((order) => (
                    <IosCard
                      key={entityId(order)}
                      sx={{ minHeight: 0, p: 1, bgcolor: "#F8F9FA", boxShadow: "none" }}
                    >
                      <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>
                            {order.orderNumber || entityId(order)}
                          </Typography>
                          <Typography sx={{ color: iosColors.muted, fontSize: 12 }}>
                            Component order • {order.status || "-"}
                          </Typography>
                        </Box>
                        <Icon sx={{ color: iosColors.subtle }}>assignment</Icon>
                      </Stack>
                    </IosCard>
                  ))
                ) : (
                  <Typography sx={{ color: iosColors.subtle, fontSize: 12.5 }}>No inventory orders yet.</Typography>
                )}
              </Stack>
            </IosCard>

            <Typography sx={{ px: 0.25, color: iosColors.muted, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
              Finished Good Lines
            </Typography>
            {items.length ? (
              items.map((line) => {
                const inventoryItem = normalizeEntity(line?.inventoryItem?.data || line?.inventoryItem);
                const lineId = entityId(line);
                const qty = Number(line.quantity || 0);
                const existing = getLineExisting(line);
                const remaining = Math.max(0, qty - existing);
                const summary = buildabilityByLine.get(String(lineId));
                const defaultBatchQty = Math.max(0, Math.min(remaining, summary?.buildableRemainingTags ?? remaining));
                const batchQty = batchQtyByLine[lineId] ?? String(defaultBatchQty || remaining || "");
                const shortageRequirements = (summary?.requirements || []).filter((req) => Number(req.shortageQty || 0) > 0);
                return (
                  <IosCard key={lineId}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between" spacing={0.75} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 900, lineHeight: 1.18 }}>
                            {inventoryItem?.sku || "Item"}
                          </Typography>
                          <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.25 }}>
                            {inventoryItem?.name || "No item name"}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                          <Chip size="small" variant="outlined" label={`Qty ${qty}`} />
                          <Chip size="small" variant="outlined" label={`${existing}/${qty}`} />
                          <Chip
                            size="small"
                            color={(summary?.shortageKinds?.length || summary?.missingBuildSheet) ? "warning" : "success"}
                            label={`Buildable ${summary?.buildableUnits ?? "-"} / ${qty}`}
                          />
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={line.isRepairReplace ? "R&R customer core" : "Stock core"} />
                        <Chip size="small" color={remaining ? "warning" : "success"} label={remaining ? `${remaining} to generate` : "Generated"} />
                        {summary?.missingBuildSheet ? <Chip size="small" color="warning" label="No build sheet" /> : null}
                        {(summary?.shortageKinds || []).map((kind) => <Chip key={kind} size="small" color="warning" label={`Short ${kind}`} />)}
                      </Stack>
                      {shortageRequirements.length ? (
                        <Typography sx={{ color: iosColors.orangeText, fontSize: 12.2, fontWeight: 800 }}>
                          {shortageRequirements.map((req) => {
                            const issued = Number(req.issuedToWorkOrderWip || 0);
                            return `${req.sku || req.name || "Item"} short ${req.shortageQty} (shelf ${req.available || 0}${issued > 0 ? `, WO WIP ${issued}` : ""})`;
                          }).join(" · ")}
                        </Typography>
                      ) : null}
                      <TextField
                        size="small"
                        label="Batch qty"
                        value={batchQty}
                        inputProps={{ inputMode: "decimal" }}
                        onChange={(event) => setBatchQtyByLine((previous) => ({ ...previous, [lineId]: event.target.value }))}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Icon>qr_code_2</Icon>}
                        disabled={generateMutation.isPending || remaining <= 0}
                        onClick={() => generateMutation.mutate({ lineId, quantity: Math.min(remaining, Math.floor(Number(batchQty) || 0)) })}
                        sx={primaryActionSx}
                      >
                        {remaining > 0 ? "Generate Batch" : "Complete"}
                      </Button>
                    </Stack>
                  </IosCard>
                );
              })
            ) : (
              <IosCard>
                <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No items on this work order.</Typography>
              </IosCard>
            )}

            <Typography sx={{ px: 0.25, color: iosColors.muted, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
              Generated Tags
            </Typography>
            {units.length ? (
              units.map((unit) => {
                const unitId = entityId(unit);
                const stage =
                  latestStageByUnit.get(String(unit.documentId || "")) ||
                  latestStageByUnit.get(String(unit.id || ""));
                const unitProgressStatus = unit?.lifecycleSummary?.progressStatus || unit?.progressStatus;
                const completed = isCompletedStatus(stage?.status) || isCompletedUnitProgress(unitProgressStatus);
                return (
                  <IosCard key={unitId || unit.id}>
                    <Stack direction="row" justifyContent="space-between" spacing={0.75} alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 900 }}>
                          {unit.optionalTag || unit.serialNumber || `Unit ${unitId || unit.id || ""}`}
                        </Typography>
                        <Typography sx={{ color: iosColors.muted, fontSize: 12.25 }}>
                          {stage?.stage?.name || stage?.status || unitProgressStatus || "Not started"}
                        </Typography>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip size="small" color={completed ? "success" : stage ? "info" : "default"} label={completed ? "Completed" : stage ? "In progress" : "Not started"} />
                        <Button
                          size="small"
                          disabled={!unitId}
                          onClick={() => {
                            if (unitId) navigate(`/m/internal/valve-bodies/${unitId}`);
                          }}
                          sx={{ textTransform: "none", fontWeight: 900 }}
                        >
                          Open
                        </Button>
                      </Stack>
                    </Stack>
                  </IosCard>
                );
              })
            ) : (
              <IosCard>
                <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No tags generated yet.</Typography>
              </IosCard>
            )}
          </>
        ) : (
          <>
            <IosCard>
              <Stack spacing={0.4}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 900 }}>Packaging Packet Builder</Typography>
                <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.32 }}>
                  Package each tag with the STOP sheet, QC report, label, and build sheet documentation.
                </Typography>
              </Stack>
            </IosCard>
            {packagingRows.length ? (
              packagingRows.map((row) => (
                <IosCard key={row.key}>
                  <Stack spacing={0.8}>
                    <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 900 }}>
                          {row.displayTag || "Tag required"}
                        </Typography>
                        <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>
                          {row.partNumber || "No part number"}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={row.completed ? "success" : "warning"}
                        variant="outlined"
                        label={row.completed ? "Completed" : `QC ${row.latestStage?.status || row.progressStatus || "pending"}`}
                      />
                    </Stack>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.28 }}>
                      {row.buildSheet?.title || "No build sheet"} • Tests {row.requiredTests.length} • QC {row.qualityChecks.length} • Docs {row.additionalInformation.length}
                    </Typography>
                    {row.needs.length ? (
                      <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                        {row.needs.map((need) => (
                          <Chip key={`${row.key}-${need}`} size="small" color="warning" variant="outlined" label={need} sx={{ fontWeight: 900 }} />
                        ))}
                      </Stack>
                    ) : (
                      <Chip size="small" color="success" label="Ready to package" sx={{ alignSelf: "flex-start", fontWeight: 900 }} />
                    )}
                    <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon>picture_as_pdf</Icon>}
                        disabled={!!busyKey || !row.canPrint}
                        onClick={() => setLanguagePrompt({ open: true, kind: "packet", row })}
                        sx={primaryActionSx}
                      >
                        {busyKey === `${row.key}:packet` ? "Building PDF..." : "Package PDF"}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Icon>local_shipping</Icon>}
                        disabled={!!busyKey || !row.canPrint}
                        onClick={() => setLanguagePrompt({ open: true, kind: "label", row })}
                        sx={secondaryActionSx}
                      >
                        {busyKey === `${row.key}:label` ? "Building Label..." : "4x6 Label"}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<Icon>open_in_new</Icon>}
                        disabled={!entityId(row.unit)}
                        onClick={() => {
                          const unitDocumentId = entityId(row.unit);
                          if (unitDocumentId) navigate(`/m/internal/valve-bodies/${unitDocumentId}`);
                        }}
                        sx={{ borderRadius: 1, textTransform: "none", fontWeight: 900 }}
                      >
                        Open Tag
                      </Button>
                    </Stack>
                  </Stack>
                </IosCard>
              ))
            ) : (
              <IosCard>
                <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                  No generated tags yet. Generate tags in Build before packaging.
                </Typography>
              </IosCard>
            )}
          </>
        )}
      </Stack>

      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          px: 1.25,
          pt: 0.8,
          pb: "calc(env(safe-area-inset-bottom) + 0.8rem)",
          bgcolor: "rgba(247,248,250,0.96)",
          borderTop: `1px solid ${iosColors.line}`,
          backdropFilter: "blur(14px)",
        }}
      >
        {activeTab === "build" ? (
          <Stack direction="row" spacing={0.7}>
            {canCloseCompletedBatch ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<Icon>call_split</Icon>}
                disabled={closeBatchMutation.isPending || statusIsClosed}
                onClick={() => {
                  if (window.confirm(`Close ${totalCompleted} completed tag(s) and move unfinished etched tags to a continuation WO?`)) {
                    closeBatchMutation.mutate();
                  }
                }}
                sx={primaryActionSx}
              >
                {closeBatchMutation.isPending ? "Closing..." : "Close Batch"}
              </Button>
            ) : null}
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<Icon>flag</Icon>}
              disabled={closeMutation.isPending || statusIsClosed || !allUnitsCompleted}
              onClick={() => closeMutation.mutate()}
              sx={successActionSx}
            >
              {statusIsClosed ? "Closed" : closeMutation.isPending ? "Closing..." : "Close WO"}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: iosColors.muted, fontWeight: 900, textTransform: "uppercase" }}>
                Packaging
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
                {packageReadyCount}/{packagingRows.length} tag{packagingRows.length === 1 ? "" : "s"} ready
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Icon>qr_code_2</Icon>}
              disabled={totalRemaining <= 0}
              onClick={() => {
                setActiveTab("build");
              }}
              sx={secondaryActionSx}
            >
              Build
            </Button>
          </Stack>
        )}
      </Box>

      <Dialog open={languagePrompt.open} onClose={() => setLanguagePrompt({ open: false, kind: "", row: null })} fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Document Language</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
            Choose the language for this packaging output.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleLanguageChoice("en")} sx={{ fontWeight: 900, textTransform: "none" }}>
            English
          </Button>
          <Button onClick={() => handleLanguageChoice("es")} sx={{ fontWeight: 900, textTransform: "none" }}>
            Spanish
          </Button>
        </DialogActions>
      </Dialog>
    </IosPage>
  );
}
